// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: keeps the iOS/Android home-screen widgets in step with what happens in
// the app (NATIVE_CONTRACT.md "Widgets"). The widgets read the server snapshot
// on their own schedule; this only asks the shell to redraw them now when
// something they show changed here: the timer, the task list, the scratchpad,
// or Home's to-dos (HomeWidgetsSync). A no-op outside the shell and on builds
// without widgets.

import KonvoScratchpad from '@app/features/konvo_scratchpad/state/KonvoScratchpad';
import KonvoTasks from '@app/features/konvo_tasks/state/KonvoTasks';
import {NativePlugins} from '@app/features/platform/native_mobile/NativeMobileBridge';
import {reaction} from 'mobx';

const RELOAD_DEBOUNCE_MS = 1500;
let reloadTimer: ReturnType<typeof setTimeout> | null = null;

/** Ask the shell to re-fetch the widget snapshot and redraw, once things settle. */
export function reloadNativeWidgets(): void {
	const plugin = NativePlugins.native();
	if (!plugin?.reloadWidgets) return;
	if (reloadTimer) clearTimeout(reloadTimer);
	reloadTimer = setTimeout(() => {
		reloadTimer = null;
		void plugin.reloadWidgets?.().catch(() => undefined);
	}, RELOAD_DEBOUNCE_MS);
}

function taskSignature(): string {
	return KonvoTasks.tasks.map((task) => `${task.id}:${task.status?.status ?? ''}:${task.due_date ?? ''}`).join('|');
}

export function startNativeWidgetSync(): () => void {
	if (!NativePlugins.native()) return () => {};
	const disposers = [
		reaction(
			() => `${KonvoTasks.runningEntry?.id ?? ''}:${KonvoTasks.runningEntry?.running ?? false}`,
			reloadNativeWidgets,
		),
		reaction(taskSignature, reloadNativeWidgets),
		reaction(() => KonvoScratchpad.version, reloadNativeWidgets),
	];
	return () => {
		for (const dispose of disposers) dispose();
		if (reloadTimer) clearTimeout(reloadTimer);
	};
}
